import { useCallback } from "react";
import { WebSocketMessage, WebSocketMessageType } from "../../providers/web_sockets_provider";
import { useAnalytics } from "../../hooks/analytics";
import {
  Annotation,
  AnnotationType,
  ConversationTurn,
  ConversationTurnRouting,
  ConversationTurnSchema,
} from "../../types";
import { isExactNear } from "../../lib/routing";
import { AnalyticsEvent } from "../../analytics/track";
import { setSearchInteractionSuccessful } from "../../analytics/interactions";
import { incrementInteractionCount } from "../../analytics/interaction_counter";
// @ts-expect-error - JavaScript API module without TypeScript definitions
import { JavascriptAPI } from "../../../lib/javascript_api";
import { ResultsData } from "./use_conversations_search";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types

export type ConversationsCreateData = {
  content?: string;
  questions?: Annotation[];
  articles?: Annotation[];
  routing?: ConversationTurnRouting;
  complete: boolean;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Formatting Helpers

const shouldShowContent = (routing?: ConversationTurnRouting): boolean => {
  return isExactNear(routing);
};

const getQuestions = (conversationTurn?: ConversationTurn): Annotation[] => {
  if (!conversationTurn?.content?.annotations) return [];

  // Get the first five related questions and/or suggestions
  return conversationTurn.content.annotations
    .filter((annotation) => [AnnotationType.RelatedQuestion, AnnotationType.Suggestion].includes(annotation.type))
    .slice(0, 5);
};

const getArticles = (conversationTurn?: ConversationTurn): Annotation[] => {
  if (!conversationTurn?.content?.annotations) return [];

  // Get the first five citations
  return conversationTurn.content.annotations
    .filter((annotation) => annotation.type === AnnotationType.Citation)
    .slice(0, 5);
};

export function useConversationsMessageHandler() {
  const { track } = useAnalytics();

  const getUpdateResponse = useCallback((message: WebSocketMessage): ConversationsCreateData | undefined => {
    const { routing, content } = message;
    if (shouldShowContent(routing) && content?.length) {
      // Start streaming the content to the UI
      return { content: content.trim(), routing, complete: false };
    }

    // No content to show, keep showing loading until the response is complete
    return undefined;
  }, []);

  const getCompletedResponse = useCallback(
    (message: WebSocketMessage): ConversationsCreateData | Error | undefined => {
      const { query, conversation_turn: rawConversationTurn } = message;

      // Validate and transform the conversation_turn data using Zod schema
      let conversationTurn: ConversationTurn | undefined;
      if (rawConversationTurn) {
        try {
          conversationTurn = ConversationTurnSchema.parse(rawConversationTurn);
        } catch (error) {
          return new Error(`Failed to parse conversation_turn: ${error}`);
        }
      }

      const routing = conversationTurn?.routing;
      const content = shouldShowContent(routing) ? conversationTurn?.content?.value : undefined;

      const questions = getQuestions(conversationTurn);
      const articles = getArticles(conversationTurn);

      track(AnalyticsEvent.SEARCH_RESULTS_GENERATED, {
        query,
        query_type: routing,
        query_results: {
          answer: content,
          questions,
          articles,
        },
      });

      if (isExactNear(routing)) {
        setSearchInteractionSuccessful();
      }

      // Emit the JavaScript API event for response completion
      if (routing && conversationTurn?.createdAt) {
        // Use client-side interaction counter instead of server's count
        // Only increment for completed interactions (not cancelled ones)
        const clientInteractionCount = incrementInteractionCount();

        JavascriptAPI.v0.broadcastResponseComplete({
          responseType: routing,
          createdAt: new Date(conversationTurn.createdAt * 1000).toISOString(), // Convert unix timestamp to ISO8601
          interactionCount: clientInteractionCount,
        });
      }

      return {
        content,
        questions,
        articles,
        routing,
        complete: true,
      };
    },
    [track],
  );

  const getFailedResponse = useCallback((message: WebSocketMessage): Error | undefined => {
    return new Error(message.error);
  }, []);

  const getResponse = useCallback(
    (message: WebSocketMessage): ResultsData | undefined => {
      switch (message.message) {
        case WebSocketMessageType.RequestUpdate: {
          // We don't want to revert to loading if we don't have content, so don't update
          // the state at all if we don't have content (can happen when a near query is streaming)
          const data = getUpdateResponse(message);
          if (data) {
            return { data, error: undefined };
          }
          return undefined;
        }
        case WebSocketMessageType.RequestCompleted: {
          const response = getCompletedResponse(message);
          if (response instanceof Error) {
            return { data: undefined, error: response };
          }
          return { data: response, error: undefined };
        }
        case WebSocketMessageType.RequestFailed:
          return { data: undefined, error: getFailedResponse(message) };
        default:
          return { data: undefined, error: undefined };
      }
    },
    [getCompletedResponse, getFailedResponse, getUpdateResponse],
  );

  return { getResponse };
}
